import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoginPage } from './pages/LoginPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { AddKeyPage } from './pages/AddKeyPage';
import { AddOrdersPage } from './pages/AddOrdersPage';
import { PaymentPlansPage } from './pages/PaymentPlansPage';
import { HelpPage } from './pages/HelpPage';
import { MarketStandbyPage } from './pages/MarketStandbyPage';
import { HakkimizdaPage } from './pages/HakkimizdaPage';
import { PaymentSuccessPage } from './pages/PaymentSuccessPage';
import { PaymentFailPage } from './pages/PaymentFailPage';
import { PaymentPage } from './pages/PaymentPage';
import { LegalPage } from './pages/LegalPage';
import { DistanceSalesContractPage } from './pages/DistanceSalesContractPage';
import { SettingsPage } from './pages/SettingsPage';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        color: 'var(--text-secondary)',
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  // Only show loading on initial auth check, not during form submissions
  // Check if we have a stored token to determine if this is initial load
  const hasStoredAuth = localStorage.getItem('polaris_token') !== null;
  const isInitialLoad = isLoading && hasStoredAuth;

  if (isInitialLoad) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        color: 'var(--text-secondary)',
      }}>
        Loading...
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Route accessible by both logged in and non-logged in users
// Shows Layout wrapper if authenticated, plain page otherwise
function OptionalAuthRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  // Check if we have a stored token to determine if this is initial load
  const hasStoredAuth = localStorage.getItem('polaris_token') !== null;
  const isInitialLoad = isLoading && hasStoredAuth;

  if (isInitialLoad) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        color: 'var(--text-secondary)',
      }}>
        Loading...
      </div>
    );
  }

  // If authenticated, wrap in Layout; otherwise render without Layout
  if (isAuthenticated) {
    return <Layout>{children}</Layout>;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/verify-email"
        element={<VerifyEmailPage />}
      />
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <DashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/add-key"
        element={
          <PrivateRoute>
            <AddKeyPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/add-orders"
        element={
          <PrivateRoute>
            <AddOrdersPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <ErrorBoundary>
              <SettingsPage />
            </ErrorBoundary>
          </PrivateRoute>
        }
      />
      <Route
        path="/payment-plans"
        element={
          <OptionalAuthRoute>
            <PaymentPlansPage />
          </OptionalAuthRoute>
        }
      />
      <Route
        path="/help"
        element={
          <OptionalAuthRoute>
            <HelpPage />
          </OptionalAuthRoute>
        }
      />
      <Route
        path="/market-standby"
        element={
          <PrivateRoute>
            <MarketStandbyPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/hakkimizda"
        element={
          <OptionalAuthRoute>
            <HakkimizdaPage />
          </OptionalAuthRoute>
        }
      />
      <Route
        path="/payment-success"
        element={
          <OptionalAuthRoute>
            <PaymentSuccessPage />
          </OptionalAuthRoute>
        }
      />
      <Route
        path="/payment-fail"
        element={
          <OptionalAuthRoute>
            <PaymentFailPage />
          </OptionalAuthRoute>
        }
      />
      <Route
        path="/payment"
        element={
          <OptionalAuthRoute>
            <PaymentPage />
          </OptionalAuthRoute>
        }
      />
      {/* Legal Pages - publicly accessible */}
      <Route path="/privacy-policy" element={<LegalPage type="privacy" />} />
      <Route path="/terms-of-service" element={<LegalPage type="terms" />} />
      <Route path="/return-policy" element={<LegalPage type="return" />} />
      <Route path="/cookie-policy" element={<LegalPage type="cookies" />} />
      <Route path="/data-deletion" element={<LegalPage type="dataDeletion" />} />
      <Route path="/distance-sales-contract" element={<DistanceSalesContractPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
